/**
 * InventoryTransferPage — 재고 이동 관리 (W19-C3, SC5 + 상태 전이 버튼).
 *
 * 【역할】
 * - tb_inventory_transfer 상태 전이: PENDING → COMPLETED / CANCELLED.
 * - 신규 이동 요청 생성 (create) + 선택 건 complete / cancel.
 *
 * 【주요 기능】
 * - 조회: status + productId 필터
 * - 신규 요청 Modal: from/to warehouse, product, qty, note
 * - 목록 행 선택 → complete/cancel 버튼 노출
 * - 상태 전이 직후 목록 재조회로 즉시 반영
 *
 * 【사용 훅】
 * - useFramework ($createDataSet, $transaction, $msg)
 * - useLabel (다국어)
 * - useI18nTickStore (locale 전환 re-render)
 *
 * 【Backend RPC serviceName】
 * - inventory_transfer/searchList (search)
 * - inventory_transfer/create (save)
 * - inventory_transfer/complete (save)
 * - inventory_transfer/cancel (save)
 *
 * 【DataSet 구조】
 * - ds_transfer: 이동 요청 목록 (PK transferId)
 *
 * 【사용처】 router/index.tsx — `{ path: 'inventory-transfer' }`
 * 【포팅 원본】 (없음 — Wave 15 백엔드 있었으나 프론트 미구현. Wave 19 에서 이식.)
 */
import { useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Modal, InputNumber, Select, Space, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useFramework } from '@/framework/useFramework'
import { useLabel } from '@/framework/useLabel'
import { useI18nTickStore } from '@/framework/useLocale'
import AppPage from '@/components/common/AppPage'
import DataTable from '@/components/common/DataTable'

interface TransferRow {
  transferId?: number
  fromWarehouseId?: number
  toWarehouseId?: number
  productId?: number
  qty?: number
  status?: string
  requestedBy?: string
  note?: string
}

interface WarehouseOption {
  warehouseId: number
  warehouseCode: string
  warehouseName: string
}

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'gold',
  IN_TRANSIT: 'blue',
  COMPLETED: 'green',
  CANCELLED: 'default'
}

/**
 * InventoryTransferPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function InventoryTransferPage() {
  const { $createDataSet, $transaction, $msg } = useFramework()
  const { t } = useLabel()
  useI18nTickStore((s) => s.tick)

  const ds_transfer = $createDataSet<TransferRow>([])
  const [searchForm] = Form.useForm()
  const [createOpen, setCreateOpen] = useState(false)
  const [createForm] = Form.useForm<TransferRow>()
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedKey, setSelectedKey] = useState<React.Key | null>(null)

  const loadWarehouses = async () => {
    const res = await $transaction({
      id: 'wh-lookup',
      service: 'warehouse/searchList',
      action: 'search',
      inds: { ds_search: { isActive: 1 } }
    })
    if (res.success) {
      const ds = res.data?.ds_warehouse as { rows?: WarehouseOption[] } | undefined
      setWarehouses(ds?.rows ?? [])
    }
  }

  const fn_search = async () => {
    setLoading(true)
    const res = await $transaction({
      id: 'tr-search',
      service: 'inventory_transfer/searchList',
      action: 'search',
      inds: { ds_search: searchForm.getFieldsValue() },
      outds: { ds_transfer }
    })
    setLoading(false)
    if (res.success) setSelectedKey(null)
  }

  useEffect(() => {
    loadWarehouses()
    fn_search()
  }, [])

  const fn_create = () => {
    createForm.resetFields()
    createForm.setFieldsValue({ qty: 1 })
    setCreateOpen(true)
  }

  const fn_createSubmit = async () => {
    const v = await createForm.validateFields()
    const res = await $transaction({
      id: 'tr-create',
      service: 'inventory_transfer/create',
      action: 'save',
      inds: { ds_transfer: { rows: [{ ...v, _state: 'C' }] } }
    })
    if (res.success) {
      $msg.byId('MSG_SAVE_SUCCESS')
      setCreateOpen(false)
      fn_search()
    }
  }

  const pickSelectedTransfer = (): TransferRow | null => {
    const row = ds_transfer.rows.find((r) => r._rowId === selectedKey)
    return row?.data ?? null
  }

  const fn_complete = async () => {
    const tr = pickSelectedTransfer()
    if (!tr?.transferId) return $msg.byId('MSG_SELECT_ROW')
    $msg.confirmById('MSG_CONFIRM_APPLY', async () => {
      const res = await $transaction({
        id: 'tr-complete',
        service: 'inventory_transfer/complete',
        action: 'save',
        inds: { ds_search: { transferId: tr.transferId } }
      })
      if (res.success) {
        $msg.byId('MSG_APPLY_SUCCESS')
        fn_search()
      }
    })
  }

  const fn_cancel = async () => {
    const tr = pickSelectedTransfer()
    if (!tr?.transferId) return $msg.byId('MSG_SELECT_ROW')
    $msg.confirmById('MSG_CONFIRM_CANCEL', async () => {
      const res = await $transaction({
        id: 'tr-cancel',
        service: 'inventory_transfer/cancel',
        action: 'save',
        inds: { ds_search: { transferId: tr.transferId } }
      })
      if (res.success) {
        $msg.byId('MSG_APPLY_SUCCESS')
        fn_search()
      }
    })
  }

  const warehouseLabel = (wid?: number) => {
    const w = warehouses.find((x) => x.warehouseId === wid)
    return w ? `${w.warehouseCode} — ${w.warehouseName}` : (wid ?? '-')
  }

  const visibleRows = ds_transfer.getVisibleRows()
  const columns: ColumnsType<(typeof visibleRows)[number]> = [
    { title: t('COL_TRANSFER_ID', '이동번호'), dataIndex: ['data', 'transferId'], width: 110 },
    {
      title: t('COL_FROM_WAREHOUSE', '출발창고'),
      dataIndex: ['data', 'fromWarehouseId'],
      width: 200,
      render: (v) => warehouseLabel(v)
    },
    {
      title: t('COL_TO_WAREHOUSE', '도착창고'),
      dataIndex: ['data', 'toWarehouseId'],
      width: 200,
      render: (v) => warehouseLabel(v)
    },
    { title: t('COL_PRODUCT_ID', '상품ID'), dataIndex: ['data', 'productId'], width: 110 },
    { title: t('COL_QTY', '수량'), dataIndex: ['data', 'qty'], width: 90 },
    {
      title: t('COL_STATUS', '상태'),
      dataIndex: ['data', 'status'],
      width: 120,
      render: (s: string) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag>
    },
    { title: t('COL_REQUESTED_BY', '요청자'), dataIndex: ['data', 'requestedBy'], width: 120 },
    { title: t('COL_NOTE', '비고'), dataIndex: ['data', 'note'] }
  ]

  const whOptions = warehouses.map((w) => ({
    label: `${w.warehouseCode} - ${w.warehouseName}`,
    value: w.warehouseId
  }))

  const selected = pickSelectedTransfer()
  const canTransition = selected?.status === 'PENDING' || selected?.status === 'IN_TRANSIT'

  return (
    <>
      <Alert
        type="info"
        showIcon
        message={t(
          'MSG_TRANSFER_INFO',
          '재고 이동 — PENDING 상태에서 complete/cancel 가능 (원자적 재고 보정).'
        )}
        style={{ marginBottom: 12 }}
      />
      <AppPage
        search={{
          form: searchForm,
          onSearch: fn_search,
          loading,
          content: (
            <>
              <Form.Item name="status" label={t('LBL_STATUS', '상태')}>
                <Select
                  allowClear
                  style={{ width: 140 }}
                  options={['PENDING', 'IN_TRANSIT', 'COMPLETED', 'CANCELLED'].map((s) => ({
                    label: s,
                    value: s
                  }))}
                />
              </Form.Item>
              <Form.Item name="productId" label={t('LBL_PRODUCT_ID', '상품ID')}>
                <InputNumber style={{ width: 120 }} />
              </Form.Item>
            </>
          )
        }}
        toolbar={
          <Space>
            <Button onClick={fn_search} loading={loading}>
              {t('BTN_SEARCH', '조회')}
            </Button>
            <Button type="primary" onClick={fn_create}>
              {t('BTN_NEW_TRANSFER', '신규 요청')}
            </Button>
            <Button onClick={fn_complete} disabled={!canTransition} type="primary">
              {t('BTN_COMPLETE', '완료 처리')}
            </Button>
            <Button onClick={fn_cancel} disabled={!canTransition} danger>
              {t('BTN_CANCEL_TRANSFER', '취소')}
            </Button>
          </Space>
        }
      >
        <DataTable
          rowKey={(r) => r._rowId}
          columns={columns}
          dataSource={visibleRows}
          loading={loading}
          rowSelection={{
            type: 'radio',
            selectedRowKeys: selectedKey ? [selectedKey] : [],
            onChange: (keys) => setSelectedKey(keys[0] ?? null)
          }}
        />
      </AppPage>

      <Modal
        open={createOpen}
        title={t('TTL_NEW_TRANSFER', '신규 이동 요청')}
        onOk={fn_createSubmit}
        onCancel={() => setCreateOpen(false)}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="fromWarehouseId"
            label={t('LBL_FROM_WAREHOUSE', '출발창고')}
            rules={[{ required: true }]}
          >
            <Select options={whOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item
            name="toWarehouseId"
            label={t('LBL_TO_WAREHOUSE', '도착창고')}
            rules={[{ required: true }]}
          >
            <Select options={whOptions} showSearch optionFilterProp="label" />
          </Form.Item>
          <Form.Item
            name="productId"
            label={t('LBL_PRODUCT_ID', '상품ID')}
            rules={[{ required: true }]}
          >
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item name="qty" label={t('LBL_QTY', '수량')} rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={1} />
          </Form.Item>
          <Form.Item name="note" label={t('LBL_NOTE', '비고')}>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}
